import { useMemo, useState } from "react";
import "./ActivityAdmin.css";

const STORAGE_KEY = "mbt_admin_activities";

const cityOptions = [
  { label: "New Delhi", value: "New Delhi", code: "DEL" },
  { label: "Jaipur", value: "Jaipur", code: "JAI" },
  { label: "Goa", value: "Goa", code: "GOA" },
  { label: "Mumbai", value: "Mumbai", code: "BOM" },
  { label: "Paris", value: "Paris", code: "PAR" },
  { label: "London", value: "London", code: "LON" },
  { label: "Phuket", value: "Phuket", code: "HKT" },
  { label: "Dubai", value: "Dubai", code: "DXB" },
];

const categoryOptions = [
  "Sightseeing",
  "Walking Tour",
  "Adventure",
  "Food & Experiences",
  "Culture & Heritage",
  "Transfers",
];

const seedActivities = [
  {
    id: "seed-red-fort",
    title: "Jai Hind Sound & Light Show at Red Fort",
    city: "New Delhi",
    location: "New Delhi",
    duration: "1 hour",
    category: "Culture & Heritage",
    price: 500,
    oldPrice: 900,
    rating: 4.8,
    reviews: 155,
    description: "The Red Fort sound and light show brings Delhi's history alive with dramatic narration, music, and vivid projections.",
    imageUrl: "",
  },
  {
    id: "seed-food-tour",
    title: "The Great Indian Food Tour",
    city: "New Delhi",
    location: "Old Delhi",
    duration: "3 hours",
    category: "Food & Experiences",
    price: 3585,
    oldPrice: 4200,
    rating: 4.9,
    reviews: 82,
    description: "Taste iconic street food and local favorites with a guided walk through Old Delhi's bustling lanes.",
    imageUrl: "",
  },
];

const emptyForm = {
  title: "",
  city: "",
  location: "",
  duration: "",
  category: "",
  price: "1299",
  oldPrice: "1800",
  rating: "4.8",
  reviews: "45",
  description: "",
  imageUrl: "",
};

function readActivities() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return saved.length ? saved : seedActivities;
  } catch {
    return seedActivities;
  }
}

function saveActivities(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function formatPrice(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function discountPercent(activity) {
  const oldPrice = Number(activity.oldPrice);
  const price = Number(activity.price);
  if (!oldPrice || oldPrice <= price) return 0;
  return Math.round(((oldPrice - price) / oldPrice) * 100);
}

export default function ActivityAdmin() {
  const [activeView, setActiveView] = useState("activities");
  const [activities, setActivities] = useState(readActivities);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [sort, setSort] = useState("default");

  const filteredActivities = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = activities.filter((activity) => {
      const matchesCity = cityFilter === "all" || activity.city === cityFilter;
      const haystack = `${activity.title} ${activity.city} ${activity.location} ${activity.category}`.toLowerCase();
      return matchesCity && (!term || haystack.includes(term));
    });

    if (sort === "price_low") rows = [...rows].sort((a, b) => Number(a.price) - Number(b.price));
    if (sort === "price_high") rows = [...rows].sort((a, b) => Number(b.price) - Number(a.price));
    if (sort === "rating") rows = [...rows].sort((a, b) => Number(b.rating) - Number(a.rating));
    return rows;
  }, [activities, cityFilter, search, sort]);

  const stats = useMemo(() => {
    const total = activities.length;
    const avgRating = total
      ? (activities.reduce((sum, item) => sum + Number(item.rating || 0), 0) / total).toFixed(1)
      : "0.0";
    const avgPrice = total
      ? Math.round(activities.reduce((sum, item) => sum + Number(item.price || 0), 0) / total)
      : 0;
    const cities = new Set(activities.map((item) => item.city).filter(Boolean)).size;
    return { total, avgRating, avgPrice, cities };
  }, [activities]);

  const cityStats = useMemo(() => cityOptions.map((city) => ({
    ...city,
    count: activities.filter((activity) => activity.city === city.value).length,
  })), [activities]);

  const categoryStats = useMemo(() => categoryOptions.map((category) => ({
    category,
    count: activities.filter((activity) => activity.category === category).length,
  })), [activities]);

  const topActivities = useMemo(() => (
    [...activities].sort((a, b) => Number(b.reviews || 0) - Number(a.reviews || 0)).slice(0, 5)
  ), [activities]);

  const viewTitles = {
    activities: ["Trip Activities", `${activities.length} activities`],
    cities: ["Cities", `${stats.cities} active cities`],
    categories: ["Categories", `${categoryOptions.length} categories`],
    analytics: ["Analytics", "activity performance"],
    bookings: ["Bookings", "latest activity bookings"],
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsFormOpen(true);
  };

  const openEditForm = (activity) => {
    setEditingId(activity.id);
    setForm({
      title: activity.title || "",
      city: activity.city || "",
      location: activity.location || "",
      duration: activity.duration || "",
      category: activity.category || "",
      price: String(activity.price || ""),
      oldPrice: String(activity.oldPrice || ""),
      rating: String(activity.rating || ""),
      reviews: String(activity.reviews || ""),
      description: activity.description || "",
      imageUrl: activity.imageUrl || "",
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.city || !form.price) return;

    const city = cityOptions.find((item) => item.value === form.city);
    const payload = {
      ...form,
      id: editingId || `activity-${Date.now()}`,
      cityCode: city?.code || "",
      price: Number(form.price || 0),
      oldPrice: Number(form.oldPrice || 0),
      rating: Number(form.rating || 0),
      reviews: Number(form.reviews || 0),
    };

    const nextActivities = editingId
      ? activities.map((item) => (item.id === editingId ? payload : item))
      : [payload, ...activities];

    setActivities(nextActivities);
    saveActivities(nextActivities);
    closeForm();
  };

  const handleDelete = (id) => {
    const nextActivities = activities.filter((item) => item.id !== id);
    setActivities(nextActivities);
    saveActivities(nextActivities);
  };

  const exportCSV = () => {
    const headers = ["title", "city", "location", "duration", "category", "price", "oldPrice", "rating", "reviews", "description", "imageUrl"];
    const rows = activities.map((activity) =>
      headers.map((key) => `"${String(activity[key] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "activities.csv";
    link.click();
  };

  return (
    <div className="activity-admin-shell">
      <aside className="activity-admin-sidebar">
        <div className="activity-admin-sidebar__brand">
          <span className="activity-admin-sidebar__mark" />
          <span>
            <strong>MakeBeeTrip</strong>
            <small>Admin Panel</small>
          </span>
        </div>

        <nav className="activity-admin-sidebar__nav">
          <small>Manage</small>
          <button type="button" className={activeView === "activities" ? "is-active" : ""} onClick={() => setActiveView("activities")}>Activities</button>
          <button type="button" className={activeView === "cities" ? "is-active" : ""} onClick={() => setActiveView("cities")}>Cities</button>
          <button type="button" className={activeView === "categories" ? "is-active" : ""} onClick={() => setActiveView("categories")}>Categories</button>
          <small>Reports</small>
          <button type="button" className={activeView === "analytics" ? "is-active" : ""} onClick={() => setActiveView("analytics")}>Analytics</button>
          <button type="button" className={activeView === "bookings" ? "is-active" : ""} onClick={() => setActiveView("bookings")}>Bookings</button>
        </nav>
      </aside>

      <section className="activity-admin">
      <div className="activity-admin__hero">
        <div>
          <div className="activity-admin__crumb">Home › Activities</div>
          <h2>{viewTitles[activeView][0]}</h2>
          <span>{viewTitles[activeView][1]}</span>
        </div>
        <div className="activity-admin__actions">
          <button type="button" className="activity-admin__btn activity-admin__btn--ghost" onClick={exportCSV}>
            Export CSV
          </button>
          <button type="button" className="activity-admin__btn activity-admin__btn--primary" onClick={openCreateForm}>
            Add Activity
          </button>
        </div>
      </div>

      {activeView === "activities" && (
        <>
      <div className="activity-admin__stats">
        <article>
          <small>Total Activities</small>
          <strong>{stats.total}</strong>
          <span>across {stats.cities} cities</span>
        </article>
        <article>
          <small>Avg Rating</small>
          <strong>{stats.avgRating} ★</strong>
          <span>all cities combined</span>
        </article>
        <article>
          <small>Avg Price</small>
          <strong>{formatPrice(stats.avgPrice)}</strong>
          <span>per person</span>
        </article>
        <article>
          <small>Cities Active</small>
          <strong>{stats.cities}</strong>
          <span>domestic + international</span>
        </article>
      </div>

      <div className="activity-admin__panel">
        <div className="activity-admin__panel-head">
          <h3>All Activities</h3>
          <span>{filteredActivities.length} results</span>
        </div>

        <div className="activity-admin__tools">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search title, location..."
          />
          <select value={cityFilter} onChange={(event) => setCityFilter(event.target.value)}>
            <option value="all">All Cities</option>
            {cityOptions.map((city) => (
              <option key={city.code} value={city.value}>{city.label}</option>
            ))}
          </select>
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="default">Sort: Default</option>
            <option value="rating">Rating</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
          </select>
        </div>

        <div className="activity-admin__table-wrap">
          <table className="activity-admin__table">
            <thead>
              <tr>
                <th>Activity</th>
                <th>City</th>
                <th>Duration</th>
                <th>Rating</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredActivities.map((activity) => (
                <tr key={activity.id}>
                  <td>
                    <strong>{activity.title}</strong>
                    <small>{activity.location || activity.category || "Activity"}</small>
                  </td>
                  <td><span className="activity-admin__city">{activity.city}</span></td>
                  <td>{activity.duration || "-"}</td>
                  <td><span className="activity-admin__rating">{activity.rating} ({activity.reviews})</span></td>
                  <td>
                    <strong>{formatPrice(activity.price)}</strong>
                    {activity.oldPrice ? <del>{formatPrice(activity.oldPrice)}</del> : null}
                  </td>
                  <td>
                    {discountPercent(activity) ? (
                      <span className="activity-admin__discount">{discountPercent(activity)}% off</span>
                    ) : "-"}
                  </td>
                  <td>
                    <div className="activity-admin__row-actions">
                      <button type="button" onClick={() => openEditForm(activity)}>Edit</button>
                      <button type="button" aria-label="Delete activity" onClick={() => handleDelete(activity.id)}>×</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {activeView === "cities" && (
        <div className="activity-admin__grid-panel">
          {cityStats.map((city) => (
            <article className="activity-admin__mini-card" key={city.code}>
              <small>{city.code}</small>
              <strong>{city.label}</strong>
              <span>{city.count} activities</span>
              <button type="button" onClick={() => { setCityFilter(city.value); setActiveView("activities"); }}>View Activities</button>
            </article>
          ))}
        </div>
      )}

      {activeView === "categories" && (
        <div className="activity-admin__grid-panel">
          {categoryStats.map((item) => (
            <article className="activity-admin__mini-card" key={item.category}>
              <small>Category</small>
              <strong>{item.category}</strong>
              <span>{item.count} activities</span>
              <button type="button" onClick={() => { setSearch(item.category); setActiveView("activities"); }}>View Matching</button>
            </article>
          ))}
        </div>
      )}

      {activeView === "analytics" && (
        <div className="activity-admin__panel">
          <div className="activity-admin__panel-head">
            <h3>Top Activity Performance</h3>
            <span>{topActivities.length} activities</span>
          </div>
          <div className="activity-admin__analytics-list">
            {topActivities.map((activity) => (
              <div key={activity.id}>
                <span>
                  <strong>{activity.title}</strong>
                  <small>{activity.city} · {activity.reviews} reviews</small>
                </span>
                <b>{activity.rating} ★</b>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeView === "bookings" && (
        <div className="activity-admin__empty-state">
          <strong>No activity bookings yet</strong>
          <span>Booking data will appear here after activity booking integration is connected.</span>
          <button type="button" onClick={() => setActiveView("activities")}>Manage Activities</button>
        </div>
      )}

      {isFormOpen && (
        <div className="activity-modal" onMouseDown={closeForm}>
          <form className="activity-modal__card" onSubmit={handleSave} onMouseDown={(event) => event.stopPropagation()}>
            <div className="activity-modal__head">
              <div>
                <h3>Add New Activity</h3>
                <p>{editingId ? "Update activity details" : "Create a new city activity"}</p>
              </div>
              <button type="button" onClick={closeForm}>×</button>
            </div>

            <div className="activity-modal__section-title">Basic Info</div>
            <div className="activity-modal__grid">
              <label className="activity-modal__field activity-modal__field--wide">
                <span>Activity Title *</span>
                <input name="title" value={form.title} onChange={handleChange} placeholder="e.g. Taj Mahal Sunrise Tour" required />
              </label>
              <label className="activity-modal__field">
                <span>City *</span>
                <select name="city" value={form.city} onChange={handleChange} required>
                  <option value="">Select city...</option>
                  {cityOptions.map((city) => (
                    <option key={city.code} value={city.value}>{city.label}</option>
                  ))}
                </select>
              </label>
              <label className="activity-modal__field">
                <span>Location / Area</span>
                <input name="location" value={form.location} onChange={handleChange} placeholder="e.g. Old Delhi" />
              </label>
              <label className="activity-modal__field">
                <span>Duration</span>
                <input name="duration" value={form.duration} onChange={handleChange} placeholder="e.g. 3 hours" />
              </label>
              <label className="activity-modal__field">
                <span>Category</span>
                <select name="category" value={form.category} onChange={handleChange}>
                  <option value="">Select...</option>
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="activity-modal__section-title">Pricing & Rating</div>
            <div className="activity-modal__grid activity-modal__grid--four">
              <label className="activity-modal__field">
                <span>Price (₹) *</span>
                <input name="price" value={form.price} onChange={handleChange} type="number" min="0" required />
              </label>
              <label className="activity-modal__field">
                <span>Old Price (₹)</span>
                <input name="oldPrice" value={form.oldPrice} onChange={handleChange} type="number" min="0" />
              </label>
              <label className="activity-modal__field">
                <span>Rating (0-5)</span>
                <input name="rating" value={form.rating} onChange={handleChange} type="number" min="0" max="5" step="0.1" />
              </label>
              <label className="activity-modal__field">
                <span>Reviews Count</span>
                <input name="reviews" value={form.reviews} onChange={handleChange} type="number" min="0" />
              </label>
            </div>

            <div className="activity-modal__section-title">Details</div>
            <div className="activity-modal__grid">
              <label className="activity-modal__field activity-modal__field--wide">
                <span>Description</span>
                <textarea name="description" value={form.description} onChange={handleChange} placeholder="Short description of the activity..." rows="4" />
              </label>
              <label className="activity-modal__field activity-modal__field--wide">
                <span>Image URL</span>
                <input name="imageUrl" value={form.imageUrl} onChange={handleChange} placeholder="https://..." />
              </label>
            </div>

            <div className="activity-modal__footer">
              <button type="button" className="activity-admin__btn activity-admin__btn--ghost" onClick={closeForm}>Cancel</button>
              <button type="submit" className="activity-admin__btn activity-admin__btn--primary">Save Activity</button>
            </div>
          </form>
        </div>
      )}
    </section>
    </div>
  );
}
